import React, { useEffect, useState } from 'react';
import { Button } from 'aha-components';
import { PlusIcon } from '@/components/common/icon';
import {
  PlatformIconComponent,
  getPlatformName,
  checkPhylloConfig
} from '@/biz/platform';
import PhylloSDK from '@/biz/phylloSDKInit';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { CertificationIcon } from '@/components/common/icon';
import { Tracker } from '@/utils';
import { ConnectButtonNoStyle } from '@/components/web/components/connectButtonNoStyle';
import { PlatformItems } from '@/interface/common';
import { getOrderId, isMobile } from '@/biz/tool';
import Loading from '@/components/Loading';
import { useTranslation } from 'react-i18next';
const ModalSelectPlatformContent: React.FC<{
  linkdInSkip?: boolean;
  styleType?: string;
}> = ({ linkdInSkip = false, styleType }) => {
  const { t } = useTranslation();
  const { platformList, phylloSDKConfig, loading, fetchPlatformData } =
    usePlatformStore();
  const { influencers } = usePlatformStore();
  const [newPlatformList, setNewPlatformList] = useState<any[]>([]);
  const search = window.location.search;
  const alwaysConnect = new URLSearchParams(search)?.get('alwaysConnect');

  useEffect(() => {
    fetchPlatformData();
  }, []);

  useEffect(() => {
    const linkdInData = platformList?.filter(
      (platform: any) => platform.platform === PlatformItems.LINKEDIN
    );
    const platformData = platformList?.filter(
      (platform: any) => platform.platform !== PlatformItems.LINKEDIN
    );
    if (platformList?.length > 0) {
      setNewPlatformList([...platformData, ...linkdInData]);
    }
  }, [platformList]);

  if (loading) {
    return (
      <div className="col-span-2 w-full flex justify-center items-center h-full">
        <Loading width={90} height={90} />
      </div>
    );
  }

  // 计算宽度类：只在 styleType="unbind" 时应用特殊布局
  const getWidthClass = (index: number, total: number) => {
    // 移动端统一单列显示
    if (isMobile()) {
      return 'w-full';
    }

    // 非 unbind 时，保持原来的 grid 布局行为（每行2个）
    if (styleType !== 'unbind') {
      return 'flex-[0_0_calc(50%-16px)]';
    }

    // styleType="unbind" 时的特殊布局
    const isOdd = total % 2 === 1; // 总数是否为奇数
    const lastThreeStartIndex = total - 3; // 最后三个的起始索引

    if (isOdd && index >= lastThreeStartIndex) {
      // 奇数时，最后三个各占33.33%
      return 'flex-[0_0_calc(33.333%-21.33px)]';
    } else {
      // 偶数时每行2个，或奇数时前面的每行2个
      return 'flex-[0_0_calc(50%-16px)]';
    }
  };

  return (
    <>
      {newPlatformList?.length > 0 ? (
        newPlatformList.map((platform: any, index: number) => {
          //
          const platformFound = influencers?.find((influencer: any) => {
            return influencer.platform === platform.platform;
          });
          const isConnected = platformFound && !alwaysConnect ? true : false;

          return (
            <div
              key={platform.name}
              className={`${getWidthClass(index, newPlatformList.length)} min-h-[86px] border border-[#d9d9e0] px-5 py-4 rounded-lg flex flex-col justify-center`}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div
                    className={`${platform.platform === PlatformItems.LINKEDIN ? 'w-6 h-6' : 'w-7 h-7 '}`}
                  >
                    <PlatformIconComponent
                      className={`${platform.platform === PlatformItems.LINKEDIN ? 'w-6 h-6' : 'w-7 h-7 '}`}
                      platform={platform.platform}
                    />
                  </div>
                  {isConnected && styleType !== 'unbind' ? (
                    <div className="flex items-center text-[#60646c] gap-1">
                      <img
                        className="w-6 h-6 rounded-full"
                        src={platformFound?.profilePictureLink}
                        // alt="avatar"
                      />
                      @{platformFound?.name}
                    </div>
                  ) : (
                    <div
                      className={`font-semibold ${platform.platform === PlatformItems.LINKEDIN ? 'text-[#A6A09B]' : ''}`}
                    >
                      {getPlatformName(platform.platform)}
                    </div>
                  )}
                </div>
                <ConnectBtn
                  styleType={styleType}
                  platform={platform}
                  isConnected={isConnected}
                  linkdInSkip={linkdInSkip}
                />
              </div>
              {(!isConnected || styleType === 'unbind') &&
                platform.platform === PlatformItems.LINKEDIN &&
                (linkdInSkip ? (
                  <div className="text-[#A6A09B] text-xs mt-2">
                    {t(
                      'modal.select_platform_content.linkdIn_skip_description',
                      {
                        defaultValue:
                          'Skip for now & verify later with screenshots'
                      }
                    )}
                  </div>
                ) : (
                  <div className="text-[#A6A09B] text-xs mt-2">
                    {t('modal.select_platform_content.skip_description', {
                      defaultValue:
                        'Use a screenshot for verification when claiming the order.'
                    })}
                  </div>
                ))}
            </div>
          );
        })
      ) : (
        <div className="h-[60px] w-full px-3 py-4 rounded-lg border border-[#d9d9e0] flex justify-center items-center">
          No platforms found
        </div>
      )}
    </>
  );
};

export default ModalSelectPlatformContent;

export const ConnectBtn = ({
  validateOrderId = false,
  styleType,
  platform,
  isConnected,
  urlShowModalAnalysis,
  uiStyle,
  linkdInSkip = false,
  contentRender
}: {
  validateOrderId?: boolean;
  styleType?: string;
  platform: any;
  isConnected: Boolean;
  urlShowModalAnalysis?: boolean;
  uiStyle?: string;
  contentRender?: React.ReactNode;
  linkdInSkip?: boolean;
}) => {
  const [connectLoading, setConnectLoading] = useState(false);
  const { platformList, phylloSDKConfig, loading, fetchPlatformData } =
    usePlatformStore();
  const { influencers } = usePlatformStore();

  if (platform?.platform && !platform?.id && !isConnected) {
    platform = platformList.find((p: any) => p.platform === platform.platform);
  }

  const handleConnect = async (platform: any) => {
    const platformId = platform.id;
    const getRedirectUrl = ({
      platformType,
      workPlatformId,
      onboarding,
      urlShowModalAnalysis,
      orderId
    }: {
      platformType: string;
      workPlatformId: string;
      onboarding: boolean;
      urlShowModalAnalysis?: boolean;
      orderId?: string;
    }) => {
      // console.log('hans-orderId', validateOrderId ? getOrderId() : '');
      return `${window.location.origin}/auth/${platformType}?work_platform_id=${workPlatformId}&onboarding=${onboarding}&urlShowModalAnalysis=${urlShowModalAnalysis}&orderId=${orderId}`;
    };

    const config = {
      clientDisplayName: 'Aha Creator',
      environment:
        process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging',
      userId: phylloSDKConfig.user_id,
      token: phylloSDKConfig.sdk_token,
      redirect: true,
      redirectURL: getRedirectUrl({
        platformType: platform.platform,
        workPlatformId: platformId,
        onboarding: !getOrderId() && Boolean(!influencers?.length), // 如果 orderId 存在，则一定不是 onboarding
        urlShowModalAnalysis: urlShowModalAnalysis,
        //
        orderId: validateOrderId ? getOrderId() : ''
      }),
      workPlatformId: platformId,
      singleAccount: true
    };

    if (!checkPhylloConfig(config)) {
      return;
    }
    let phylloSDK = new PhylloSDK();
    await phylloSDK.openPhylloSDK(config);
  };

  if (isConnected && styleType !== 'unbind') {
    return <CertificationIcon className="w-6 h-6 flex-shrink-0" />;
  }

  if (platform?.platform === PlatformItems.YOUTUBE) {
    return (
      <ConnectButtonNoStyle
        validateOrderId={validateOrderId}
        platform={PlatformItems.YOUTUBE}
        buttonLoading={connectLoading}
        setButtonLoading={setConnectLoading}
        onboarding={!getOrderId() && Boolean(!influencers?.length)}
      >
        <ConnectBtnCustom
          platform={platform}
          uiStyle={uiStyle}
          loading={connectLoading}
          contentRender={contentRender}
        />
      </ConnectButtonNoStyle>
    );
  } else {
    return (
      <>
        <ConnectBtnCustom
          platform={platform}
          uiStyle={uiStyle}
          loading={connectLoading}
          linkdInSkip={linkdInSkip}
          onClick={() => {
            // if (!isConnected) {
            // google 上报
            Tracker.click('connect platform');
            Tracker.click(`connect platform ${platform.platform}`);
            handleConnect(platform);
            // }
          }}
          contentRender={contentRender}
        />
      </>
    );
  }
};

const ConnectBtnCustom = ({
  platform,
  onClick,
  loading,
  uiStyle,
  linkdInSkip = false,
  contentRender
}: {
  platform: any;
  onClick?: () => void;
  loading?: boolean;
  uiStyle?: string;
  contentRender?: React.ReactNode;
  linkdInSkip?: boolean;
}) => {
  const { t } = useTranslation();
  if (contentRender) {
    return (
      <div
        onClick={() => {
          onClick && onClick();
        }}
      >
        {contentRender}
      </div>
    );
  }
  if (uiStyle === 'blackConnect') {
    return (
      <Button
        className="!w-[119px] !h-10 flex items-center justify-center"
        onClick={() => {
          onClick && onClick();
        }}
        loading={loading}
      >
        {loading ? 'Connecting...' : 'Connect'}
      </Button>
    );
  }

  if (!linkdInSkip && platform?.platform === PlatformItems.LINKEDIN) {
    return null;
  }

  return (
    <Button
      className="!border-none !box-shadow-none"
      onClick={() => {
        onClick && onClick();
      }}
      loading={loading}
    >
      <>
        {platform?.platform !== PlatformItems.LINKEDIN ? (
          <PlusIcon className="w-6 h-6 " />
        ) : (
          <div
            className="text-[#FB6011] cursor-pointer text-sm"
            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
              event?.stopPropagation();
              window.location.href = '/campaign/invites';
            }}
          >
            {t('modal.select_platform_content.skip', {
              defaultValue: 'Skip'
            })}
          </div>
        )}
      </>
    </Button>
  );
};
